import React, { useState, useRef, useEffect } from "react";
import {
  Menu,
  X,
  UserCircle,
  LogOut,
  Settings as SettingsIcon,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const menuItems = [
  { label: "Transactions", href: "/transactions" },
  { label: "Goals", href: "/goals" },
  { label: "Subscriptions", href: "/subscriptions" },
  { label: "Loans", href: "/loans" },
  { label: "Friends", href: "/friends" },
  //   { label: "Settings", href: "/settings" },
];

type HeaderProps = { compact?: boolean };

const Header: React.FC<HeaderProps> = ({ compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const logoutUser = () => {
    try {
      localStorage.removeItem("user");
      // Clear persisted filters on logout for privacy and clean state
      localStorage.removeItem("filters:transactions");
      localStorage.removeItem("filters:subscriptions");
      localStorage.removeItem("filters:loans");
      localStorage.removeItem("filters:goals");
    } catch {}
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-30 backdrop-blur border-b border-white/10 text-gray-200">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <Link to={"/dashboard"} className="text-2xl font-bold text-white">
            Budiee
          </Link>

          {!compact && (
          <nav className="hidden md:flex space-x-6 items-center">
            {menuItems.map(({ label, href }) => (
              <Link
                key={label}
                to={href}
                className="hover:text-white font-medium text-gray-200"
              >
                {label}
              </Link>
            ))}

            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="ml-4 flex items-center hover:text-white text-gray-200"
              >
                <UserCircle size={28} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 card-glass shadow-lg rounded-md py-2 z-50 border border-white/10">
                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-200 hover:bg-white/10"
                  >
                    <SettingsIcon className="mr-2" size={16} />
                    Profile Settings
                  </Link>
                  <button
                    onClick={() => {
                      logoutUser();
                    }}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-200 hover:bg-white/10"
                  >
                    <LogOut className="mr-2" size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </nav>
          )}

          {/* Mobile Toggle Button */}
          {!compact && (
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-200"
              aria-label="Toggle Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {!compact && isOpen && (
        <div className="md:hidden px-4 pb-4 space-y-2 bg-slate-950/80 border-t border-white/10">
          {menuItems.map(({ label, href }) => (
            <Link
              key={label}
              to={href}
              className="block hover:text-white font-medium text-gray-200"
              onClick={() => setIsOpen(false)}
            >
              {label}
            </Link>
          ))}

          <div className="border-t pt-2 mt-2">
            <Link
              to="/profile"
              className="block text-gray-200 hover:text-white font-medium py-1"
              onClick={() => setIsOpen(false)}
            >
              Profile Settings
            </Link>
            <button
              onClick={() => {
                setIsOpen(false);
                logoutUser();
              }}
              className="block text-left w-full text-gray-200 hover:text-white font-medium py-1"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
